
// import React, { CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
// import { useTranslation } from "react-i18next";
// import { useLocation } from 'react-router';
// import { colors, css, fonts, frames, glyphs, useMediaQuery } from "../../../../Styles";
// import { ButtonList, PageHeader, NewSelect } from "../../../../Components";
// import { ButtonTypes, NewFieldStyles, ListData_ValueTypes, ListStyle_Types, TableStyles_Types } from "../../../../Definitions";
// import * as XLSX from 'xlsx';

import { useQuery } from "@apollo/client";
import { useLocation } from "react-router";
import { useAppSelector } from "../../../../Store";
import { GET_ARCHIVED_REPORTS, ReportWithData } from "../../../../Services/Agrisys_GqlDataCaller/ReportDataCalls/GQLCalls/getArchivedReportsCall";
import { getCoreRowModel, getFilteredRowModel, getSortedRowModel, useReactTable } from "@tanstack/react-table";
import { GenericDataParameters, useColumnConstructor } from "../../../../Components/Tables/_Tables/useColumnConstructor";
import { ButtonTypes, ListData_ValueTypes, NewFieldStyles, TableStyles_Types } from "../../../../Definitions";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactiveTable } from "../../../../Components/Tables/reactiveTableRenderer";

import { colors, frames, glyphs } from '../../../../Styles';
import { LocaleFarmDate } from "../../../../Definitions/localeFarmDate";
import { NewSelect, PageHeader, ProgressBar } from "../../../../Components";
import { cloneDeep } from "lodash";
import { Graph_QL } from "../../../../Services";
import { useReport } from "../../../../Services/Agrisys_GqlDataCaller/ReportDataCalls/useReport";
import { Pages } from "../../../../Navigation/root";

import { exportTableToCsv } from "./_Reports/exportTableToCsv";
import { exportTableToExcel } from "./_Reports/exportTableToXml";
import { NewButtonList } from "../../../../Components/BaseComponents/newButton";
const ignoredKeys = ['consumption',"feedcurve_milestones", "__typename", "cycle", "condition", "animal_type"]

export default function Report() {
  const location = useLocation()
  const reportId = location.state?.params.selected;
  const [selectedReport, setReport] = useState<number | undefined>(reportId);

  const farm = useAppSelector(({ activeData }) => activeData.farm) ?? "";
  const domain = useMemo(() => Graph_QL.ObjToString({ domain: farm }), [farm]);
  const connection = useMemo(() => Graph_QL.create({ domain: farm }), [farm]);
  
  const {data: archivedReportState, call: getArchivedReportsCall} = useReport(connection)

  useEffect(() => {
    if (!archivedReportState[domain]?.data) {
      getArchivedReportsCall({offset: 0, fetchAmount: 50, ifFetchData: false})
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[])

  const archivedReports = useMemo(() => {
    if (archivedReportState && archivedReportState[domain]) {
      return archivedReportState[domain].data.map((report) => ({label: report.name ?? "", id: report.reportId}));
    }
    return [];
  },[archivedReportState, domain])
  
  useEffect(() => {
    if (!selectedReport) {
      if (location.state?.params?.selected) {
        setReport(location.state.params.selected);
      } else if (archivedReports.length) {
        setReport(archivedReports[0].id);
      }
    }
  }, [archivedReports, location.state?.params.selected, selectedReport])

  
  const {loading, error, data} = useQuery<ReportWithData>(GET_ARCHIVED_REPORTS, {variables: {domain: farm, input: {ids: selectedReport, offset: 0, fetchAmount: 1}, ifFetchData: true}})

  const {t} = useTranslation();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reportData = useMemo(() => {
    if (selectedReport !== undefined && data && data.report.archived[0] && data.report.archived[0].reportId === selectedReport && "data" in data.report.archived[0] && Array.isArray(data.report.archived[0].data)) {
      return data.report.archived[0].data;
    }
    return [];
  },[data, selectedReport])

  const {accessorData, tableData, consumptionAccessors} = useMemo(() => {
    const accessorMap = new Map<string, GenericDataParameters<any>>()
    const consumptionAccessors = new Set<string>()
    const tableData = cloneDeep(reportData).map((reportRow) => {
      Object.keys(reportRow).forEach((key) => {          
        if (accessorMap.get(key) || key.includes("_id") || ignoredKeys.includes(key)) {
          return
        }  
        accessorMap.set(key, {
        headerValue: t(`report.reportColumn.${key}`, {defaultValue: key.replace(/_/g," ")}),
        accessor:key,
        type: ListData_ValueTypes.String,
        sort: true,
        styleOverrideOnValue: false,
      })
      })

      if ("birthday" in reportRow && reportRow.birthday && typeof reportRow.birthday === "string") {
        reportRow["birthday"] = new LocaleFarmDate(reportRow["birthday"]).toDateForDisplay()
      } 
      if ("milestone_day" in reportRow && reportRow.milestone_day && typeof reportRow.milestone_day === "string") {
        reportRow["milestone_day"] = new LocaleFarmDate(reportRow["milestone_day"]).toDateForDisplay()
      }

      const reportRowConsumptionData: {[key: string]: JSX.Element} = {}
      const consumptionAcc = {total: 0, progressed: 0}
      if ("consumption" in reportRow && Array.isArray(reportRow.consumption)) {
        reportRow.consumption.forEach((consumptionRow) => {
          if (consumptionRow && typeof consumptionRow === "object" && "timestamp" in consumptionRow) {
            consumptionAccessors.add(String(consumptionRow.timestamp));
            accessorMap.set(String(consumptionRow.timestamp), {
              headerValue: new LocaleFarmDate(consumptionRow.timestamp).toDateForDisplay(),
              accessor: String(consumptionRow.timestamp),
              type: ListData_ValueTypes.ProgressBar,
              sort: true,
              styleOverrideOnValue: false,
            })

          const consumptionTotal = +(consumptionRow.expected_amount / 1000).toFixed(2);
          const consumptionProgressed  = +(consumptionTotal - (consumptionRow.accumulated_amount / 1000)).toFixed(2);
          consumptionAcc.total = consumptionAcc.total + consumptionRow.expected_amount;
          consumptionAcc.progressed = consumptionAcc.progressed + consumptionRow.expected_amount-consumptionRow.accumulated_amount;
          reportRowConsumptionData[String(consumptionRow.timestamp)] = <ProgressBar total={consumptionTotal} progressed={consumptionProgressed} extension={{hasExtension: true}} /> 
          } 
        });
        accessorMap.set("totalPeriodConsumption", {
         headerValue: t(`report.reportColumn.totalPeriodConsumption`, {defaultValue: 'Total period consumption'}),
         accessor: 'totalPeriodConsumption',
         type: ListData_ValueTypes.ProgressBar,
         sort: true
        })
      }
      reportRowConsumptionData['totalPeriodConsumption'] = <ProgressBar total={+(consumptionAcc.total/1000).toFixed(2)} progressed={+(consumptionAcc.progressed/1000).toFixed(2)} extension={{hasExtension: true}}/> 
      return {...reportRow, ...reportRowConsumptionData}
    })
    return {accessorData: Array.from(accessorMap.values()), tableData, consumptionAccessors};
  },[reportData, t]);
  
    const columns = useColumnConstructor({accessorData, tableStyle: TableStyles_Types.primaryTable, canEdit: false})
  
    const table = useReactTable({
      columns: columns,
      data: tableData,
      state: {
        columnOrder: ["animal_number", "group_name", "system_name", "totalPeriodConsumption", ...Array.from(consumptionAccessors), "animal_group_name", "milestone_name"]
      },
      getCoreRowModel: getCoreRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getSortedRowModel: getSortedRowModel(),
    })
  
  if (error ) {
    return <>{"An error occurred when attempting to load the report data"}</>
  }

  if (loading && !selectedReport) {
    return "loading";
  }
  const consumptionReportLabel = t("report.consumption_report", {defaultValue: "consumption_report"});
  return (
    <frames.Page_inner>
      <PageHeader
        topHeader={t(`menuHeaders.${Pages.report}`, { defaultValue: "report" })}
        subSelect={{
          options: archivedReports,
          onChange: (e) => { setReport(e?.id ?? location.state.params.selected) },
          defaultValue: archivedReports.find(report => report.id === selectedReport)
        }}
      />
      <div style={{display: "flex", flexDirection: "column", width: "100%", overflow: "hidden"}}>
        <div style={{ display: "flex", flexDirection: "row-reverse", padding: "0px 20px" }}>
          <NewButtonList
            contents={[
              t("buttons.excel", { defaultValue: "excel" }),
              t("buttons.csv", { defaultValue: "csv" }),
            ]}
            onClick={(i) => {
              if (i === 0) { exportTableToExcel(tableData, `${consumptionReportLabel}.xlsx`, consumptionReportLabel, accessorData) }
              if (i === 1) { exportTableToCsv(tableData, `${consumptionReportLabel}.csv`, accessorData) }
            }}
            buttonType={ButtonTypes.tableButton}
            startEnhancers={[ glyphs.Close({}), glyphs.Close({}) ]}
          />
        </div>
        <div style={{height: "fit-content", maxHeight: "100%", boxSizing: "border-box", position: "relative", backgroundColor: colors.greyLightSecondary, border: "5px solid "+colors.greyPrimary, borderRadius: "10px", overflow: "hidden"}}>
          <ReactiveTable table={table} tableType={TableStyles_Types.primaryTable} emptyText={t("report.table.emptyText", {defaultValue: "No report data"})} />
        </div>
      </div>
    </frames.Page_inner>
  );
}
